<template>
  <section class="event-detail-page">
    <v-progress-linear
      v-if="state.isLoadingEventDetail"
      indeterminate
    />

    <AppPageTitleBar
      v-if="state.eventDetail"
      class="is-hidden-sm-up"
      :title="pageTitle"
      :back-to="{ name: 'events-list' }"
      back-label="Natrag"
    >
      <template #actions>
        <v-btn
          v-if="canEditEvents && !state.eventDetail.isArchived"
          class="button button--secondary button--small event-detail-page__edit-button"
          :to="{ name: 'event-edit', params: { eventId: state.eventDetail.id } }"
          variant="outlined"
        >
          Uredi događaj
        </v-btn>
      </template>
    </AppPageTitleBar>

    <section
      v-if="state.eventDetail"
      class="page-stack grid-stack grid-gap-page-section"
    >
      <article class="page-panel page-panel--mobile-unframed event-detail-card grid-stack grid-gap-16 grid-gap-sm-24">
        <div class="event-detail-card__toolbar grid grid-sm-up grid-row-gap-0 grid-column-gap-16 align-items-center">
          <router-link
            class="back-link event-detail-card__back col-12 col-sm-4"
            :to="{ name: 'events-list' }"
            aria-label="Natrag"
          >
            <v-icon
              icon="mdi-arrow-left"
              size="20"
            />
            <span>Natrag</span>
          </router-link>

          <div class="event-detail-card__brand col-12 col-sm-4">
            <img
              v-if="eventBrandLogo"
              class="event-detail-card__brand-logo"
              :src="eventBrandLogo"
              :alt="`Logotip ${eventBrandName}`"
            />
            <span
              v-else
              class="event-detail-card__brand-fallback text-hint text-center"
            >{{ eventBrandName }}</span>
          </div>

          <v-btn
            v-if="canEditEvents && !state.eventDetail.isArchived"
            class="button button--secondary button--small event-detail-page__edit-button col-12 col-sm-4"
            :to="{ name: 'event-edit', params: { eventId: state.eventDetail.id } }"
            variant="outlined"
          >
            Uredi događaj
          </v-btn>
        </div>

        <header class="event-detail-card__header">
          <div class="event-detail-card__title-row">
            <h2 class="event-detail-card__title">{{ state.eventDetail.name }}</h2>
            <EventStatusChip
              class="event-detail-card__status"
              :is-archived="state.eventDetail.isArchived"
              :status="getEventStatus(state.eventDetail)"
            />
          </div>

          <div class="event-detail-card__offline-control">
            <label
              class="event-detail-card__offline-label"
              for="event-offline-switch"
            >Spremi offline</label>
            <v-switch
              id="event-offline-switch"
              class="event-detail-card__offline-switch"
              :model-value="state.isEventAvailableOffline"
              :loading="state.isUpdatingOfflineAvailability"
              :disabled="state.isUpdatingOfflineAvailability"
              base-color="var(--color-control-border)"
              color="navigationActive"
              density="compact"
              hide-details
              inset
              @update:model-value="setEventOfflineAvailabilityAsync"
            />
            <span
              class="event-detail-card__offline-status"
              role="status"
              aria-live="polite"
            >
              <template v-if="state.isEventAvailableOffline">
                <v-icon
                  icon="mdi-check-circle-outline"
                  size="14"
                />
                Dostupno offline
              </template>
            </span>
          </div>
        </header>

        <dl class="event-detail-card__summary">
          <div
            v-for="summaryItem in eventSummaryItems"
            :key="summaryItem.label"
            class="event-detail-card__summary-item"
          >
            <dt>{{ summaryItem.label }}</dt>
            <dd>{{ summaryItem.value }}</dd>
          </div>
        </dl>

        <div class="event-detail-card__links grid grid-gap-14">
          <router-link
            v-if="canViewInvitees"
            class="event-detail-card__link col-12 col-sm-6"
            :to="{ name: 'event-detail-invitees', params: { eventId: state.eventDetail.id } }"
          >
            <span>Uzvanici ({{ state.eventDetail.invitees.length }})</span>
            <v-icon
              icon="mdi-arrow-right"
              size="18"
            />
          </router-link>

          <router-link
            class="event-detail-card__link col-12 col-sm-6"
            :to="{ name: 'event-detail-vehicles', params: { eventId: state.eventDetail.id } }"
          >
            <span>Vozila ({{ state.eventDetail.vehicles.length }})</span>
            <v-icon
              icon="mdi-arrow-right"
              size="18"
            />
          </router-link>
        </div>

        <div class="event-detail-card__sections grid-stack grid-gap-10">
          <section
            v-for="section in state.detailSections"
            :key="section.key"
            class="event-detail-section"
          >
            <button
              class="event-detail-section__trigger"
              type="button"
              @click="toggleDetailSection(section.key)"
            >
              <span>{{ section.title }}</span>
              <v-icon
                :icon="isDetailSectionOpen(section.key) ? 'mdi-chevron-up' : 'mdi-chevron-down'"
                size="20"
              />
            </button>

            <div
              v-if="isDetailSectionOpen(section.key)"
              class="event-detail-section__content grid-stack grid-gap-14 grid-gap-sm-20"
            >
              <template v-if="section.key === 'questionnaires'">
                <div class="event-detail-section__group grid-stack grid-gap-10">
                  <h4>Popis upitnika</h4>
                  <div class="event-detail-section__table grid-stack grid-gap-0">
                    <div
                      class="event-detail-section__row event-detail-section__row--header grid grid-sm-up grid-row-gap-6 grid-row-gap-sm-0 grid-column-gap-20"
                    >
                      <span class="col-12 col-sm-5">Naziv upitnika</span>
                      <span class="col-12 col-sm-7">URL upitnika</span>
                    </div>
                    <div
                      v-for="questionnaire in eventQuestionnaires"
                      :key="questionnaire.id"
                      class="event-detail-section__row grid grid-row-gap-6 grid-row-gap-sm-0 grid-column-gap-20"
                    >
                      <span class="col-12 col-sm-5">{{ questionnaire.name }}</span>
                      <a
                        class="col-12 col-sm-7"
                        :href="questionnaire.url"
                      >
                        {{ questionnaire.url }}
                      </a>
                    </div>
                  </div>
                </div>

                <div class="event-detail-section__group grid-stack grid-gap-10">
                  <h4>Rezultati upitnika</h4>
                  <div class="event-detail-section__table grid-stack grid-gap-0">
                    <div
                      class="event-detail-section__row event-detail-section__row--header grid grid-sm-up grid-row-gap-6 grid-row-gap-sm-0 grid-column-gap-20"
                    >
                      <span class="col-12 col-sm-5">Naziv rezultata upitnika</span>
                      <span class="col-12 col-sm-7">URL rezultata upitnika</span>
                    </div>
                    <div
                      v-for="questionnaireResult in eventQuestionnaireResults"
                      :key="questionnaireResult.id"
                      class="event-detail-section__row grid grid-row-gap-6 grid-row-gap-sm-0 grid-column-gap-20"
                    >
                      <span class="col-12 col-sm-5">{{ questionnaireResult.name }}</span>
                      <a
                        class="col-12 col-sm-7"
                        :href="questionnaireResult.url"
                      >
                        {{ questionnaireResult.url }}
                      </a>
                    </div>
                  </div>
                </div>
              </template>

              <template v-else-if="section.key === 'documents'">
                <p class="event-detail-section__note">
                  Dokumenti se potpisuju na profilu uzvanika.
                </p>
                <div class="event-detail-section__table event-detail-section__table--single grid-stack grid-gap-0">
                  <div class="event-detail-section__row event-detail-section__row--header grid grid-sm-up">
                    <span class="col-12">Naziv</span>
                  </div>
                  <div
                    v-for="document in state.eventDetail.documents"
                    :key="document.id"
                    class="event-detail-section__row grid"
                  >
                    <span class="col-12">{{ document.name }}</span>
                  </div>
                </div>
              </template>

              <template v-else-if="section.key === 'activities'">
                <p class="event-detail-section__note">
                  Aktivnosti se potpisuju na profilu uzvanika.
                </p>
                <div class="event-detail-section__table event-detail-section__table--single grid-stack grid-gap-0">
                  <div class="event-detail-section__row event-detail-section__row--header grid grid-sm-up">
                    <span class="col-12">Naziv</span>
                  </div>
                  <div
                    v-for="activity in state.eventDetail.activities"
                    :key="activity.id"
                    class="event-detail-section__row grid"
                  >
                    <span class="col-12">
                      {{ activity.name }}<template v-if="activity.customFieldValue">: {{ activity.customFieldValue }}</template>
                    </span>
                  </div>
                </div>
              </template>

              <p
                v-else
                class="event-detail-section__note event-detail-section__note--plain"
              >
                {{ state.eventDetail.comment }}
              </p>
            </div>
          </section>
        </div>
      </article>
    </section>
  </section>
</template>

<script setup>
import { computed, onMounted, reactive } from 'vue'
import { useRoute } from 'vue-router'

import AppPageTitleBar from '../../components/AppPageTitleBar.vue'
import EventStatusChip from '../../components/EventStatusChip.vue'
import { hasPermission, permissionNames } from '../../auth/authorization'
import { formatDate, getEventBrandLogo, getEventBrandName, getEventStatus } from '../../helpers'
import {
  getActivityEventAsync,
  isEventAvailableOfflineAsync,
  prepareEventForOfflineAsync,
  removeEventFromOfflineAsync,
} from '../../services/offlineService'
import { showErrorToast, showRequestErrorToast } from '../../store/appStore'

const route = useRoute()

const state = reactive({
  eventDetail: null,
  isLoadingEventDetail: false,
  isEventAvailableOffline: false,
  isUpdatingOfflineAvailability: false,
  expandedSections: [],
  detailSections: [
    {
      key: 'questionnaires',
      title: 'Upitnici',
    },
    {
      key: 'documents',
      title: 'Popis dokumenata na događaju',
    },
    {
      key: 'activities',
      title: 'Popis aktivnosti na događaju',
    },
    {
      key: 'notes',
      title: 'Napomene',
    },
  ],
})

const pageTitle = computed(() => route.meta.title)
const canEditEvents = computed(() => hasPermission(permissionNames.eventsEdit))
const canViewInvitees = computed(() => hasPermission(permissionNames.eventsInviteesAccess))
const eventBrandLogo = computed(() => getEventBrandLogo(state.eventDetail?.brandCode))
const eventBrandName = computed(() => getEventBrandName(state.eventDetail?.brandCode))
const eventQuestionnaires = computed(() => state.eventDetail.questionnaires.filter(({ type }) => type === 'form'))
const eventQuestionnaireResults = computed(() => state.eventDetail.questionnaires.filter(({ type }) => type === 'result'))
const eventSummaryItems = computed(() => {
  const event = state.eventDetail

  if (!event) {
    return []
  }

  return [
    {
      label: 'Marka',
      value: getEventBrandName(event.brandCode),
    },
    {
      label: 'Lokacija',
      value: event.location,
    },
    {
      label: 'Datum početka',
      value: formatDate(event.startDate),
    },
    {
      label: 'Vrijeme početka',
      value: event.startTime,
    },
    {
      label: 'Datum završetka',
      value: formatDate(event.endDate),
    },
    {
      label: 'Vrijeme završetka',
      value: event.endTime,
    },
    {
      label: 'Kreirao',
      value: event.createdBy,
    },
  ]
})

onMounted(loadEventDetailAsync)

async function loadEventDetailAsync () {
  state.isLoadingEventDetail = true

  try {
    const [eventDetail, isEventAvailableOffline] = await Promise.all([
      getActivityEventAsync(route.params.eventId),
      isEventAvailableOfflineAsync(route.params.eventId),
    ])

    state.eventDetail = eventDetail
    state.isEventAvailableOffline = isEventAvailableOffline
  } catch (error) {
    console.error('Failed to load event detail.', error)
    showRequestErrorToast(error, 'Detalje događaja nije bilo moguće učitati.')
  } finally {
    state.isLoadingEventDetail = false
  }
}

async function setEventOfflineAvailabilityAsync (isAvailableOffline) {
  state.isUpdatingOfflineAvailability = true

  try {
    if (isAvailableOffline) {
      state.eventDetail = await prepareEventForOfflineAsync(state.eventDetail.id)
      state.isEventAvailableOffline = true
      return
    }

    const wasRemoved = await removeEventFromOfflineAsync(state.eventDetail.id)

    if (!wasRemoved) {
      showErrorToast('Događaj ima nesinkronizirane promjene i ne može se ukloniti iz offline pohrane.')
      return
    }

    state.isEventAvailableOffline = false
  } catch (error) {
    console.error('Failed to update event offline availability.', error)
    showRequestErrorToast(error, 'Offline dostupnost događaja nije bilo moguće promijeniti.')
  } finally {
    state.isUpdatingOfflineAvailability = false
  }
}

function toggleDetailSection (sectionKey) {
  if (state.expandedSections.includes(sectionKey)) {
    state.expandedSections = state.expandedSections.filter((key) => key !== sectionKey)
    return
  }

  state.expandedSections.push(sectionKey)
}

function isDetailSectionOpen (sectionKey) {
  return state.expandedSections.includes(sectionKey)
}
</script>

<style
  scoped
  lang="scss"
>
.event-detail-page {
  width: 100%;
  overflow-x: hidden;

  &__edit-button {
    width: 144px;
    min-width: 144px;
    height: 32px;
    padding: 0 12px;
    background: var(--color-white);
    font-size: 11px;
    line-height: 16px;
    text-transform: uppercase;

    @include breakpoint-up("sm") {
      width: auto;
      min-width: 132px;
      height: 40px;
      padding: 0 16px;
      background: transparent;
      font-size: clamp-bp("sm", "xl", 12, 13);
      line-height: normal;
    }
  }
}

.event-detail-card {
  width: 100%;

  &__toolbar {
    .event-detail-page__edit-button {
      justify-self: end;
    }
  }

  &__brand {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 80px;
  }

  &__brand-logo {
    width: min(100%, 120px);
    height: 80px;
    object-fit: contain;
  }

  &__brand-fallback {
    color: var(--color-text-muted);
  }

  &__header {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: clamp-bp("sm", "xl", 16, 24);
  }

  &__offline-control {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px;
  }

  &__offline-label {
    font-weight: 600;
    line-height: 24px;
    cursor: pointer;
  }

  &__offline-switch {
    flex: 0 0 44px;

    :deep(.v-selection-control) {
      min-height: 24px;
    }

    :deep(.v-selection-control__wrapper) {
      width: 44px;
      height: 24px;
    }

    :deep(.v-switch__track) {
      min-width: 44px;
      height: 24px;
      padding: 2px;
      opacity: 1;
    }

    :deep(.v-switch__thumb) {
      width: 20px;
      height: 20px;
      transform: none;
    }
  }

  &__offline-status {
    display: flex;
    align-items: center;
    gap: 4px;
    color: var(--color-success);
    font-size: 12px;
    font-weight: 400;
    line-height: 18px;
  }

  &__title-row {
    display: flex;
    flex-wrap: nowrap;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;

    @include breakpoint-up("sm") {
      flex-wrap: wrap;
      align-items: center;
      justify-content: flex-start;
      gap: 10px;
    }
  }

  &__title {
    flex: 1 1 auto;
    min-width: 0;
    margin: 0;

    @include breakpoint-up("sm") {
      flex: 0 1 auto;
    }
  }

  &__status {
    margin-top: 2px;

    @include breakpoint-up("sm") {
      margin-top: 0;
    }
  }

  &__summary {
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
    margin: 0;
    padding: 16px;
    border: 1px solid var(--color-border);
    border-radius: 12px;
    background: var(--color-white);
    box-shadow: var(--shadow-control);

    @include breakpoint-up("sm") {
      flex-wrap: nowrap;
      gap: 14px;
      padding: 18px 20px;
      border-radius: 8px;
    }
  }

  &__summary-item {
    flex: 0 0 calc((100% - 16px) / 2);
    min-width: 0;

    @include breakpoint-up("sm") {
      flex: 1 1 0;
    }

    dt {
      margin: 0 0 4px;
      color: var(--color-text-muted);
      font-size: clamp-bp("sm", "xl", 11, 12);
      font-weight: 300;
      line-height: clamp-bp("sm", "xl", 16, 18);

      @include breakpoint-up("sm") {
        margin-bottom: 6px;
      }
    }

    dd {
      margin: 0;
      color: var(--color-black);
      font-size: clamp-bp("sm", "xl", 12, 13);
      font-weight: 300;
      line-height: clamp-bp("sm", "xl", 18, 20);
      overflow-wrap: anywhere;
    }
  }

  &__link {
    display: flex;
    align-items: center;
    justify-content: space-between;
    min-height: 58px;
    padding: 16px;
    border: 1px solid var(--color-border);
    border-radius: 12px;
    background: var(--color-white);
    color: var(--color-black);
    font-size: 15px;
    font-weight: 600;
    line-height: 22px;
    text-decoration: none;
    box-shadow: var(--shadow-control);

    @include breakpoint-up("sm") {
      min-height: 72px;
      padding: 20px;
      border-radius: 8px;
      font-size: clamp-bp("sm", "xl", 17, 18);
      line-height: clamp-bp("sm", "xl", 24, 26);
    }

    span {
      min-width: 0;
      overflow-wrap: anywhere;
    }
  }
}

.event-detail-section {
  &__trigger {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    min-height: 40px;
    padding: 0;
    border: 0;
    background: transparent;
    color: var(--color-black);
    cursor: pointer;
    font: inherit;
    font-size: 14px;
    font-weight: 600;
    line-height: 20px;
    text-align: left;

    @include breakpoint-up("sm") {
      min-height: 42px;
      font-size: clamp-bp("sm", "xl", 15, 16);
      line-height: clamp-bp("sm", "xl", 22, 24);
    }
  }

  &__content {
    padding: 10px 0 16px;

    @include breakpoint-up("sm") {
      padding: 14px 0 18px;
    }
  }

  &__group {
    h4 {
      margin: 0;
      color: var(--color-black);
      font-size: clamp-bp("sm", "xl", 13, 14);
      font-weight: 600;
      line-height: clamp-bp("sm", "xl", 20, 22);
    }
  }

  &__note {
    display: inline-flex;
    width: fit-content;
    margin: 0;
    padding: 7px 12px;
    border-radius: 6px;
    background: var(--color-info-background);
    color: var(--color-info-text);
    font-size: clamp-bp("sm", "xl", 12, 13);
    font-weight: 300;
    line-height: clamp-bp("sm", "xl", 18, 20);

    &--plain {
      display: block;
      width: auto;
      background: var(--color-surface-hover);
      color: var(--color-text-body);
    }
  }

  &__table {
    padding: 8px 14px;
    border-radius: 6px;
    background: var(--color-surface-hover);

    @include breakpoint-up("sm") {
      padding: 12px 20px;
    }
  }

  &__row {
    min-height: 0;
    padding: 10px 0;
    border-bottom: 1px solid var(--color-border);
    color: var(--color-text-body);
    font-size: clamp-bp("sm", "xl", 12, 13);
    font-weight: 300;
    line-height: clamp-bp("sm", "xl", 18, 20);

    @include breakpoint-up("sm") {
      min-height: 43px;
      padding: 12px 0;
    }

    &:last-child {
      border-bottom: 0;
    }

    &--header {
      min-height: 34px;
      color: var(--color-text-muted);
    }

    a {
      color: var(--color-text-body);
      overflow-wrap: anywhere;
    }
  }
}
</style>
